import { styled } from "@pigment-css/react";
import type {
  BarChart,
  ChartSection as ChartSectionSchema,
  LifetimeCard as LifetimeCardSchema,
} from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { Card } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import type { BarEmphasis } from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import { CardHeader } from "./CardHeader";
import { ChartCaption, ChartViz, PeriodBarChart } from "./ChartSection";
import { KpiPair } from "./KpiPair";
import { OrderScopeEntries } from "./OrderScopeEntries";
import { SentimentColor } from "./SentimentColor";
import { useChartDisplay } from "./useChartDisplay";

const PauseHighlight = styled(SentimentColor)(({ theme }) => ({
  variants: [
    {
      props: { sentiment: "negative" },
      style: { color: theme.fieldSalesTwo.colors.charts.primary },
    },
  ],
}));

function PauseChart({ section }: { section: ChartSectionSchema }) {
  const { chart } = section;
  const { leadSeries } = useChartDisplay(chart);
  const pauseSeries = chart.y.find((series) => series !== leadSeries && series.sentiment === "negative");
  const isPause = (row: BarChart["data"][number]) =>
    pauseSeries !== undefined && row[leadSeries.field] == null && row[pauseSeries.field] != null;

  const pauseInLeadSlot: BarChart = pauseSeries
    ? {
        ...chart,
        data: chart.data.map((row) => (isPause(row) ? { ...row, [leadSeries.field]: row[pauseSeries.field] } : row)),
      }
    : chart;
  const barEmphasis = pauseSeries && chart.data.map((row): BarEmphasis => (isPause(row) ? "highlighted" : "muted"));

  return (
    <ChartViz>
      {section.caption && (
        <ChartCaption>
          {section.caption.map((fragment, index) => (
            <PauseHighlight key={index} sentiment={fragment.sentiment ?? "neutral"}>
              {fragment.text}
            </PauseHighlight>
          ))}
        </ChartCaption>
      )}
      <PeriodBarChart chart={pauseInLeadSlot} granularity={section.granularity} barEmphasis={barEmphasis} />
    </ChartViz>
  );
}

export function LifetimeCard({ part }: { part: LifetimeCardSchema }) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderCard");

  return (
    <Card nested>
      <CardHeader label={part.label} headline={part.headline} />
      <Divider />
      <KpiPair items={part.kpis} />
      <PauseChart section={part.chart} />
      <Divider />
      <OrderScopeEntries section={part.orders} allOrdersTitle={t("allOrders")} orderListEyebrow={part.label?.text} />
    </Card>
  );
}
